import { useState, useEffect, useMemo } from 'react';
import { Link, Zap, User, Users, Calendar } from 'lucide-react';
import Modal from './Modal';
import { normalizeAssignment } from './LinkItem';
import { useUsers } from '../hooks/useUsers';
import { useAuth } from '../hooks/useAuth';

const FREQUENCIES = ['daily', 'weekly', 'monthly'];
const DAYS_OF_WEEK = [
  { value: null, label: 'Any day' },
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

export default function LinkModal({ isOpen, onClose, onSave, editItem = null }) {
  const { user } = useAuth();
  const { supervisors } = useUsers();

  // Build list of assignable users: current user + supervisors
  const assignableUsers = useMemo(() => {
    const users = [];
    if (user) {
      users.push({
        uid: user.uid,
        displayName: user.displayName || 'Me',
        firstName: user.displayName?.split(' ')[0] || 'Me'
      });
    }
    supervisors.forEach(s => {
      users.push({
        uid: s.uid,
        displayName: s.displayName,
        firstName: s.firstName || s.displayName?.split(' ')[0] || 'User'
      });
    });
    return users;
  }, [user, supervisors]);

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'tracker',
    pinned: false,
    assignments: {},
    isQuickLink: false
  });
  const [assignAll, setAssignAll] = useState(false);
  const [allFrequency, setAllFrequency] = useState('daily');

  useEffect(() => {
    if (editItem) {
      // Handle migration from old format
      let assignments = editItem.assignments || {};
      if (!editItem.assignments && editItem.owner && editItem.frequency) {
        assignments = { [editItem.owner]: editItem.frequency };
      }

      const isQuickLink = editItem.isQuickLink || false;

      setFormData({
        name: editItem.name || '',
        url: editItem.url || '',
        type: editItem.type || 'tracker',
        pinned: editItem.pinned || false,
        assignments,
        isQuickLink
      });

      // Check if all assigned users have same frequency
      const assignedUserIds = Object.keys(assignments);
      if (assignedUserIds.length >= 2 && !isQuickLink) {
        const frequencies = assignedUserIds.map(uid => normalizeAssignment(assignments[uid]).frequency);
        const allSameFreq = frequencies.every(f => f === frequencies[0]);
        if (allSameFreq && assignedUserIds.length === assignableUsers.length) {
          setAssignAll(true);
          setAllFrequency(frequencies[0]);
        } else {
          setAssignAll(false);
        }
      } else {
        setAssignAll(false);
      }
    } else {
      setFormData({
        name: '',
        url: '',
        type: 'tracker',
        pinned: false,
        assignments: {},
        isQuickLink: false
      });
      setAssignAll(false);
      setAllFrequency('daily');
    }
  }, [editItem, isOpen, assignableUsers.length]);

  const toggleUserAssignment = (userId) => {
    const newAssignments = { ...formData.assignments };
    if (newAssignments[userId]) {
      delete newAssignments[userId];
    } else {
      const freq = assignAll ? allFrequency : 'daily';
      newAssignments[userId] = { frequency: freq, scheduledDay: null };
    }
    setFormData({ ...formData, assignments: newAssignments });
  };

  const setUserFrequency = (user, frequency) => {
    const current = normalizeAssignment(formData.assignments[user]);
    // If changing away from weekly, clear scheduledDay
    const scheduledDay = frequency === 'weekly' ? current.scheduledDay : null;
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        [user]: { frequency, scheduledDay }
      }
    });
  };

  const setUserScheduledDay = (user, scheduledDay) => {
    const current = normalizeAssignment(formData.assignments[user]);
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        [user]: { frequency: current.frequency, scheduledDay }
      }
    });
  };

  const handleAssignAllChange = (checked) => {
    setAssignAll(checked);
    if (checked) {
      const newAssignments = {};
      assignableUsers.forEach(u => {
        newAssignments[u.uid] = { frequency: allFrequency, scheduledDay: null };
      });
      setFormData({
        ...formData,
        assignments: newAssignments
      });
    }
  };

  const handleAllFrequencyChange = (frequency) => {
    setAllFrequency(frequency);
    if (assignAll) {
      // Preserve existing scheduled days when changing frequency
      const newAssignments = {};
      assignableUsers.forEach(u => {
        const existingDay = frequency === 'weekly'
          ? normalizeAssignment(formData.assignments[u.uid]).scheduledDay
          : null;
        newAssignments[u.uid] = { frequency, scheduledDay: existingDay };
      });
      setFormData({
        ...formData,
        assignments: newAssignments
      });
    }
  };

  const handleQuickLinkChange = (checked) => {
    if (checked) {
      setFormData({ ...formData, isQuickLink: true, assignments: {} });
      setAssignAll(false);
    } else {
      setFormData({ ...formData, isQuickLink: false });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.isQuickLink && Object.keys(formData.assignments).length === 0) {
      alert('Please assign at least one person or mark as Quick Link');
      return;
    }
    onSave(formData);
    onClose();
  };

  const isUserAssigned = (user) => !!formData.assignments[user];
  const getUserAssignmentData = (user) => normalizeAssignment(formData.assignments[user]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editItem ? 'Edit Link' : 'Add New Link'}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* Name Field */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            Name
          </label>
          <input
            type="text"
            placeholder="e.g., Daily Equipment Check"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            className="w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all placeholder:text-text-muted"
          />
        </div>

        {/* URL Field */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            URL
          </label>
          <input
            type="url"
            placeholder="https://..."
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            required
            className="w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all placeholder:text-text-muted"
          />
        </div>

        {/* Type Field */}
        <div>
          <label className="block text-sm font-medium mb-2 text-text-secondary">
            Type
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="w-full py-3 px-4 bg-surface-elevated border border-border rounded-xl text-sm text-text-primary outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all cursor-pointer"
          >
            <option value="tracker">Tracker</option>
            <option value="webapp">Web App</option>
            <option value="dashboard">Dashboard</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Assignments Section */}
        <div>
          <label className="block text-sm font-medium mb-3 text-text-secondary">
            Assignments
          </label>

          <div className="rounded-xl border border-border overflow-hidden bg-surface-elevated/50">
            {/* Quick Link option */}
            <button
              type="button"
              onClick={() => handleQuickLinkChange(!formData.isQuickLink)}
              className={`w-full flex items-center gap-3 p-4 border-b border-border cursor-pointer transition-all ${
                formData.isQuickLink
                  ? 'bg-accent-muted'
                  : 'bg-transparent hover:bg-surface-hover'
              }`}
            >
              <div className={`p-2 rounded-lg ${formData.isQuickLink ? 'bg-accent/20' : 'bg-surface-card'}`}>
                <Zap size={16} className={formData.isQuickLink ? 'text-accent' : 'text-text-tertiary'} />
              </div>
              <div className="flex-1 text-left">
                <span className="text-sm font-medium text-text-primary">Quick Link</span>
                <p className="text-xs text-text-tertiary">No check-off required</p>
              </div>
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                formData.isQuickLink
                  ? 'bg-accent border-accent'
                  : 'border-border'
              }`}>
                {formData.isQuickLink && (
                  <svg className="w-3 h-3 text-surface-base" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </button>

            {/* Individual user assignments */}
            {assignableUsers.map((userObj, idx) => {
              const assignmentData = isUserAssigned(userObj.uid) ? getUserAssignmentData(userObj.uid) : null;
              const isWeekly = assignmentData?.frequency === 'weekly';

              return (
                <div
                  key={userObj.uid}
                  className={`p-4 transition-all ${
                    idx !== assignableUsers.length - 1 ? 'border-b border-border' : ''
                  } ${isUserAssigned(userObj.uid) ? 'bg-accent-muted/50' : ''} ${formData.isQuickLink ? 'opacity-40 pointer-events-none' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => toggleUserAssignment(userObj.uid)}
                      disabled={assignAll || formData.isQuickLink}
                      className="flex items-center gap-3 cursor-pointer bg-transparent border-none"
                    >
                      <div className={`p-2 rounded-lg ${isUserAssigned(userObj.uid) ? 'bg-accent/20' : 'bg-surface-card'}`}>
                        <User size={16} className={isUserAssigned(userObj.uid) ? 'text-accent' : 'text-text-tertiary'} />
                      </div>
                      <span className="text-sm font-medium text-text-primary">{userObj.firstName}</span>
                    </button>
                    {isUserAssigned(userObj.uid) && !formData.isQuickLink && (
                      <select
                        value={assignmentData.frequency}
                        onChange={(e) => setUserFrequency(userObj.uid, e.target.value)}
                        disabled={assignAll}
                        className="py-2 px-3 bg-surface-card border border-border rounded-lg text-sm text-text-primary cursor-pointer"
                      >
                        {FREQUENCIES.map((freq) => (
                          <option key={freq} value={freq}>
                            {freq.charAt(0).toUpperCase() + freq.slice(1)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Day picker for weekly items */}
                  {isUserAssigned(userObj.uid) && !formData.isQuickLink && isWeekly && (
                    <div className="mt-3 ml-11 flex items-center gap-2">
                      <Calendar size={14} className="text-weekly" />
                      <span className="text-xs text-text-secondary">Show in Daily on:</span>
                      <select
                        value={assignmentData.scheduledDay ?? ''}
                        onChange={(e) => setUserScheduledDay(userObj.uid, e.target.value === '' ? null : parseInt(e.target.value))}
                        className="py-1.5 px-2 bg-surface-card border border-border rounded-lg text-xs text-text-primary cursor-pointer"
                      >
                        {DAYS_OF_WEEK.map((day) => (
                          <option key={day.label} value={day.value ?? ''}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Assign all option */}
            {assignableUsers.length > 1 && (
              <div className={`p-4 bg-surface-card border-t border-border ${formData.isQuickLink ? 'opacity-40 pointer-events-none' : ''}`}>
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => handleAssignAllChange(!assignAll)}
                    disabled={formData.isQuickLink}
                    className="flex items-center gap-3 cursor-pointer bg-transparent border-none"
                  >
                    <div className={`p-2 rounded-lg ${assignAll ? 'bg-accent/20' : 'bg-surface-elevated'}`}>
                      <Users size={16} className={assignAll ? 'text-accent' : 'text-text-tertiary'} />
                    </div>
                    <span className="text-sm font-medium text-text-primary">Assign all</span>
                  </button>
                  {assignAll && !formData.isQuickLink && (
                    <select
                      value={allFrequency}
                      onChange={(e) => handleAllFrequencyChange(e.target.value)}
                      className="py-2 px-3 bg-surface-elevated border border-border rounded-lg text-sm text-text-primary cursor-pointer"
                    >
                      {FREQUENCIES.map((freq) => (
                        <option key={freq} value={freq}>
                          {freq.charAt(0).toUpperCase() + freq.slice(1)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Day pickers for all users when weekly */}
                {assignAll && !formData.isQuickLink && allFrequency === 'weekly' && (
                  <div className="mt-3 ml-11 space-y-2">
                    {assignableUsers.map((userObj) => {
                      const assignmentData = getUserAssignmentData(userObj.uid);
                      return (
                        <div key={userObj.uid} className="flex items-center gap-2">
                          <Calendar size={14} className="text-weekly" />
                          <span className="text-xs text-text-secondary">{userObj.firstName}:</span>
                          <select
                            value={assignmentData?.scheduledDay ?? ''}
                            onChange={(e) => setUserScheduledDay(userObj.uid, e.target.value === '' ? null : parseInt(e.target.value))}
                            className="py-1.5 px-2 bg-surface-card border border-border rounded-lg text-xs text-text-primary cursor-pointer"
                          >
                            {DAYS_OF_WEEK.map((day) => (
                              <option key={day.label} value={day.value ?? ''}>
                                {day.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pin option */}
        <button
          type="button"
          onClick={() => setFormData({ ...formData, pinned: !formData.pinned })}
          className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
            formData.pinned
              ? 'bg-accent-muted border-accent/30'
              : 'bg-surface-elevated/50 border-border hover:bg-surface-hover'
          }`}
        >
          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
            formData.pinned
              ? 'bg-accent border-accent'
              : 'border-border'
          }`}>
            {formData.pinned && (
              <svg className="w-3 h-3 text-surface-base" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-sm font-medium text-text-primary">Pin to top of dashboard</span>
        </button>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-5 bg-surface-hover text-text-secondary border border-border rounded-xl text-sm font-medium cursor-pointer hover:bg-surface-active hover:text-text-primary transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 py-3 px-5 bg-gradient-to-r from-accent to-orange-hover text-surface-base rounded-xl text-sm font-semibold cursor-pointer shadow-lg shadow-accent/20 hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            {editItem ? 'Save Changes' : 'Add Link'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
